import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Collapse,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Science as ScienceIcon,
  Medication as MedicationIcon,
  Restaurant as RestaurantIcon,
  Vaccines as VaccinesIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useBirdLogs, useCreateBirdLog, useDeleteBirdLog } from '../hooks/useBirdLogs';
import { useBirds } from '../hooks/useBirds';
import { BirdLog } from '../types';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

const BirdLogsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewDialog, setViewDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState<BirdLog | null>(null);
  const [logToDelete, setLogToDelete] = useState<{ id: number; product: string } | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [filters, setFilters] = useState({
    birds_id: searchParams.get('birds_id') ? Number(searchParams.get('birds_id')) : null,
    type: searchParams.get('type') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    birds_id: '',
    type: '',
    product_name: '',
    quantity: '',
    unit: '',
    notes: '',
    administered_at: new Date().toISOString().split('T')[0],
  });

  // React Query hooks
  const queryParams = React.useMemo(
    () => ({
      page: paginationModel.page + 1,
      per_page: paginationModel.pageSize,
      birds_id: filters.birds_id || undefined,
      type: filters.type || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
    }),
    [paginationModel.page, paginationModel.pageSize, filters]
  );

  const { data: logsData, error, isFetching: logsFetching, refetch } = useBirdLogs(queryParams);
  const { data: birdsData, isFetching: birdsFetching } = useBirds();
  const createMutation = useCreateBirdLog();
  const deleteMutation = useDeleteBirdLog();

  // Sync filters with URL
  React.useEffect(() => {
    const params = new URLSearchParams();
    if (filters.birds_id) params.set('birds_id', filters.birds_id.toString());
    if (filters.type) params.set('type', filters.type);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const logs = logsData?.data || [];
  const totalCount = logsData?.meta?.total || 0;

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Bird Logs Data
            </Typography>
            <Typography variant="body2" paragraph>
              {error instanceof Error
                ? error.message
                : 'An error occurred while fetching bird logs. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }
  // Get type icon and color
  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'feed':
        return { icon: <RestaurantIcon />, color: '#4caf50' };
      case 'vitamins':
        return { icon: <ScienceIcon />, color: '#ff9800' };
      case 'medication':
        return { icon: <MedicationIcon />, color: '#f44336' };
      case 'vaccine':
        return { icon: <VaccinesIcon />, color: '#2196f3' };
      default:
        return { icon: <ScienceIcon />, color: '#9e9e9e' };
    }
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      minWidth: 70,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'bird',
      headerName: 'Bird',
      flex: 1.5,
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value, row) => row.bird?.name || 'N/A',
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const config = getTypeConfig(params.value);
        return (
          <Chip
            icon={config.icon}
            label={params.value}
            size="small"
            sx={{
              backgroundColor: `${config.color}15`,
              color: config.color,
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          />
        );
      },
    },
    {
      field: 'product_name',
      headerName: 'Product',
      flex: 1.5,
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value) => value || 'N/A',
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 1,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value, row) => (value && row.unit ? `${value} ${row.unit}` : value || 'N/A'),
    },
    {
      field: 'administered_at',
      headerName: 'Date Administered',
      flex: 1.5,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value) => {
        try {
          return format(new Date(value), 'MMM dd, yyyy HH:mm');
        } catch {
          return value;
        }
      },
    },
    {
      field: 'administrator',
      headerName: 'Administered By',
      flex: 1.2,
      minWidth: 140,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value, row) => row.administrator?.name || 'N/A',
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1.5,
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value) => value || 'No notes',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedLog(params.row);
              setViewDialog(true);
            }}
            sx={{ color: '#ff9800' }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setLogToDelete({
                id: params.row.id,
                product: params.row.product_name || params.row.type,
              });
              setDeleteDialog(true);
            }}
            sx={{ color: '#f44336' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Handlers
  const handleAddLog = async () => {
    try {
      await createMutation.mutateAsync({
        birds_id: Number(formData.birds_id),
        type: formData.type as any,
        product_name: formData.product_name || null,
        quantity: formData.quantity || null,
        unit: formData.unit || null,
        notes: formData.notes || null,
        administered_at: formData.administered_at,
      });
      setAddDialog(false);
      resetForm();
    } catch (error: any) {
      console.error('Failed to create bird log:', error);
    }
  };

  const handleDelete = async () => {
    if (!logToDelete) return;
    try {
      await deleteMutation.mutateAsync(logToDelete.id);
      setDeleteDialog(false);
      setLogToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete bird log:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      birds_id: '',
      type: '',
      product_name: '',
      quantity: '',
      unit: '',
      notes: '',
      administered_at: new Date().toISOString().split('T')[0],
    });
  };

  const handleApplyFilters = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleClearFilters = () => {
    setFilters({
      birds_id: null,
      type: '',
      date_from: '',
      date_to: '',
    });
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(logsFetching || birdsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Page Header */}
      <Box
        sx={{
          mb: 3.5,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(33,150,243,0.15)', color: '#2196f3', width: 52, height: 52 }}>
          <ScienceIcon fontSize="medium" />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            Bird Logs
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
            Track medication, feed, vaccine and treatment records for each bird batch
          </Typography>
        </Box>
      </Box>

      {/* Filters Section */}
      <Paper sx={{ mb: 3, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.05)',
            },
          }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon sx={{ color: '#ff9800' }} />
            <Typography variant="h6" fontWeight={600} sx={{ color: '#ffffff' }}>
              Filter Logs
            </Typography>
          </Box>
          <IconButton
            sx={{
              transform: filtersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              color: '#ff9800',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        <Collapse in={filtersExpanded}>
          <Box sx={{ p: 2.5, pt: 0 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Bird"
                  value={filters.birds_id || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      birds_id: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                >
                  <MenuItem value="">All Birds</MenuItem>
                  {birdsData?.data?.map((bird) => (
                    <MenuItem key={bird.id} value={bird.id}>
                      {bird.identifier}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Type"
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="feed">Feed</MenuItem>
                  <MenuItem value="vitamins">Vitamins</MenuItem>
                  <MenuItem value="medication">Medication</MenuItem>
                  <MenuItem value="vaccine">Vaccine</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
                  color: '#ffffff',
                  '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)' },
                }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': { borderColor: '#f57c00', backgroundColor: 'rgba(255, 152, 0, 0.08)' },
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 'calc(100vh - 200px)', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
        <DataGrid
          rows={logs}
          columns={columns}
          loading={logsFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalCount}
          disableRowSelectionOnClick
          autoHeight={false}
          sx={{
            border: 'none',
            height: '100%',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              borderBottom: '1px solid rgba(255, 152, 0, 0.3)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#ffffff',
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255, 152, 0, 0.2)',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(255, 152, 0, 0.3)',
              bgcolor: 'rgba(255, 152, 0, 0.05)',
            },
          }}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 152, 0, 0.3)' }}>
          <Box display="flex" alignItems="center" gap={1}>
            {selectedLog && getTypeConfig(selectedLog.type).icon}
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#ff9800' }}>
              Log Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 152, 0, 0.8)' }}>
                  Bird
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLog.bird?.identifier || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 152, 0, 0.8)' }}>
                  Type
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    icon={getTypeConfig(selectedLog.type).icon}
                    label={selectedLog.type}
                    size="small"
                    sx={{
                      backgroundColor: `${getTypeConfig(selectedLog.type).color}15`,
                      color: getTypeConfig(selectedLog.type).color,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Product Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLog.product_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Quantity
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLog.quantity && selectedLog.unit
                    ? `${selectedLog.quantity} ${selectedLog.unit}`
                    : selectedLog.quantity || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Date Administered
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {format(new Date(selectedLog.administered_at), 'MMMM dd, yyyy HH:mm')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Administered By
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLog.administrator?.name || 'N/A'}
                </Typography>
              </Grid>
              {selectedLog.notes && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedLog.notes}
                  </Typography>
                </Grid>
              )}
              {selectedLog.type === 'feed' && selectedLog.total_feed_cost && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Cost per KG
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      ₵{selectedLog.cost_per_kg}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Cost
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      ₵{selectedLog.total_feed_cost}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(255, 152, 0, 0.3)',
            backgroundColor: 'rgba(255, 152, 0, 0.05)',
            px: 3,
            pb: 3,
          }}
        >
          <Button onClick={() => setViewDialog(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 152, 0, 0.3)', color: '#ff9800' }}>
          Add Bird Log
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                required
                label="Bird"
                value={formData.birds_id}
                onChange={(e) => setFormData({ ...formData, birds_id: e.target.value })}
              >
                {birdsData?.data?.map((bird) => (
                  <MenuItem key={bird.id} value={bird.id}>
                    {bird.identifier}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                required
                label="Type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <MenuItem value="feed">Feed</MenuItem>
                <MenuItem value="vitamins">Vitamins</MenuItem>
                <MenuItem value="medication">Medication</MenuItem>
                <MenuItem value="vaccine">Vaccine</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Product Name"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Quantity"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Unit"
                placeholder="e.g., kg, ml, tablets"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="date"
                label="Date Administered"
                value={formData.administered_at}
                onChange={(e) => setFormData({ ...formData, administered_at: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(255, 152, 0, 0.3)',
            backgroundColor: 'rgba(255, 152, 0, 0.05)',
            px: 3,
            pb: 3,
          }}
        >
          <Button
            onClick={() => setAddDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddLog}
            disabled={!formData.birds_id || !formData.type || createMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)' },
            }}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Log'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        slotProps={{ paper: { sx: { border: '1px solid rgba(244,67,54,0.35)', borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(244, 67, 54, 0.3)', color: '#f44336' }}>
          Delete Bird Log
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#ffffff', mt: 2 }}>
            Are you sure you want to delete this log entry?
            {logToDelete && (
              <Typography component="span" fontWeight="bold">
                {' '}
                ({logToDelete.product})
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid rgba(244, 67, 54, 0.3)',
            backgroundColor: 'rgba(244, 67, 54, 0.05)',
            px: 3,
            pb: 3,
          }}
        >
          <Button
            onClick={() => setDeleteDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)' },
            }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BirdLogsPage;
